import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingDown, TrendingUp, RefreshCw, Target, BarChart3, AlertTriangle, Save, Database, LineChart, Clock, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { StockTechnicalChart } from '@/components/StockTechnicalChart';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface SectorData {
  sector: string;
  symbol: string;
  performance: {
    day1: number;
    week1: number;
    month1: number;
    year1: number;
  };
  athGap: number;
}

interface StockPick {
  ticker: string;
  current: number;
  high52w: number;
  athGap: number;
}

interface SectorRecommendation {
  sector: string;
  stocks: StockPick[];
}

function PerformanceBar({ value, label }: { value: number; label: string }) {
  const isPositive = value >= 0;
  const absValue = Math.min(Math.abs(value), 50); // cap at 50% for display
  const barWidth = (absValue / 50) * 100;

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-14 text-right text-muted-foreground font-mono">{label}</span>
      <div className="flex-1 h-5 bg-muted/30 rounded-sm relative overflow-hidden">
        {/* Center line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border z-10" />
        {isPositive ? (
          <div
            className="absolute left-1/2 top-0 bottom-0 bg-emerald-500/70 rounded-r-sm transition-all duration-500"
            style={{ width: `${barWidth / 2}%` }}
          />
        ) : (
          <div
            className="absolute right-1/2 top-0 bottom-0 bg-red-500/70 rounded-l-sm transition-all duration-500"
            style={{ width: `${barWidth / 2}%` }}
          />
        )}
      </div>
      <span className={`w-16 text-right font-mono font-semibold ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
        {value >= 0 ? '+' : ''}{value.toFixed(1)}%
      </span>
    </div>
  );
}

function ATHGapBar({ stock, onSelect }: { stock: StockPick; onSelect: (ticker: string) => void }) {
  const gapAbs = Math.min(Math.abs(stock.athGap), 60);
  const barWidth = (gapAbs / 60) * 100;

  return (
    <div
      className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-muted/40 rounded-md px-1 -mx-1 transition-colors group"
      onClick={() => onSelect(stock.ticker)}
    >
      <span className="w-14 font-mono font-bold text-sm text-foreground group-hover:text-primary transition-colors flex items-center gap-1">
        {stock.ticker}
        <LineChart className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
      </span>
      <div className="flex-1 relative">
        <div className="h-6 bg-muted/30 rounded-sm overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-red-600/80 to-red-400/60 rounded-sm transition-all duration-700"
            style={{ width: `${barWidth}%` }}
          />
        </div>
        <div className="absolute inset-0 flex items-center px-2">
          <span className="text-[10px] font-mono text-white drop-shadow-sm">
            ${stock.current.toFixed(0)} → ${stock.high52w.toFixed(0)}
          </span>
        </div>
      </div>
      <span className="w-16 text-right font-mono font-bold text-red-500 text-sm">
        {stock.athGap.toFixed(1)}%
      </span>
    </div>
  );
}

interface SectorPerformancePanelProps {
  restoreId?: string | null;
  onHelpClick?: () => void;
}

export function SectorPerformancePanel({ restoreId, onHelpClick }: SectorPerformancePanelProps = {}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sectorData, setSectorData] = useState<SectorData[]>([]);
  const [recommendations, setRecommendations] = useState<SectorRecommendation[]>([]);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [underperformFilter, setUnderperformFilter] = useState<'both' | 'month1' | 'year1'>('both');
  const [sortByGap, setSortByGap] = useState(true);
  const [savedFromDb, setSavedFromDb] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);

  const filteredRecommendations = recommendations
    .filter(rec => {
      const sector = sectorData.find(s => s.sector === rec.sector);
      if (!sector) return true;
      if (underperformFilter === 'month1') return sector.performance.month1 < 0;
      if (underperformFilter === 'year1') return sector.performance.year1 < 0;
      return true;
    })
    .sort((a, b) => {
      if (!sortByGap) return 0;
      const maxGapA = Math.max(...a.stocks.map(s => Math.abs(s.athGap)));
      const maxGapB = Math.max(...b.stocks.map(s => Math.abs(s.athGap)));
      return maxGapB - maxGapA;
    });

  // Load saved sector data on mount or when restoreId changes
  useEffect(() => {
    const loadSaved = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = supabase
        .from('sector_analyses')
        .select('*')
        .eq('user_id', user.id);

      if (restoreId) {
        query = query.eq('id', restoreId);
      } else {
        query = query.order('created_at', { ascending: false }).limit(1);
      }

      const { data } = await query.single();

      if (data) {
        setSectorData((data.sector_data as unknown as SectorData[]) || []);
        setRecommendations((data.recommendations as unknown as SectorRecommendation[]) || []);
        setFetchedAt(data.fetched_at);
        setSavedFromDb(true);
      }
    };
    loadSaved();
  }, [restoreId]);

  const saveToDatabase = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Please sign in to save data');

      const { error } = await supabase.from('sector_analyses').insert({
        user_id: user.id,
        sector_data: JSON.parse(JSON.stringify(sectorData)),
        recommendations: JSON.parse(JSON.stringify(recommendations)),
        fetched_at: fetchedAt || new Date().toISOString(),
      });
      if (error) throw error;

      setSavedFromDb(true);
      toast({ title: 'Saved', description: 'Sector analysis saved to database' });
    } catch (e: any) {
      toast({ title: 'Save Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('sector-performance');
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to fetch');

      setSectorData(data.sectorPerformance || []);
      setRecommendations(data.stockRecommendations || []);
      setFetchedAt(data.fetchedAt);
      setSavedFromDb(false);
      toast({ title: 'Sector Data Loaded', description: `${data.sectorPerformance?.length || 0} sectors analysed` });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const sortedBySector = (period: 'day1' | 'week1' | 'month1' | 'year1') =>
    [...sectorData].sort((a, b) => a.performance[period] - b.performance[period]);

  return (
    <>
    <Card className="border-amber-500/30 shadow-lg mt-10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/10 rounded-xl">
              <Target className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                Other Strategies — Sector Rotation
                {onHelpClick && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button onClick={(e) => { e.stopPropagation(); onHelpClick(); }} className="text-muted-foreground hover:text-primary transition-colors">
                        <HelpCircle className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Open help for Sector Performance</TooltipContent>
                  </Tooltip>
                )}
                {savedFromDb && fetchedAt && (
                  <Badge variant="outline" className="text-[10px] font-mono gap-1 border-primary/30 text-primary">
                    <Clock className="h-3 w-3" />
                    {new Date(fetchedAt).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {' '}
                    {new Date(fetchedAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Sector performance analysis & undervalued core stocks based on distance from 52-week highs
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sectorData.length > 0 && (
              <Button onClick={saveToDatabase} disabled={saving} variant="outline" size="sm" className="gap-2">
                <Save className={`h-4 w-4 ${saving ? 'animate-pulse' : ''}`} />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            <Button onClick={fetchData} disabled={loading} variant="outline" size="sm" className="gap-2">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : sectorData.length > 0 ? 'Refresh' : 'Load Sector Data'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {sectorData.length === 0 && !loading && (
          <div className="text-center py-12 text-muted-foreground">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Click "Load Sector Data" to fetch real-time sector performance</p>
            <p className="text-xs mt-1">Analysis of 11 S&P 500 sectors with stock-level ATH gap recommendations</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Fetching sector data from Yahoo Finance...</p>
            <p className="text-xs text-muted-foreground mt-1">This may take 30–60 seconds</p>
          </div>
        )}

        {sectorData.length > 0 && !loading && (
          <div className="space-y-6">
            {/* Sector Performance Tabs */}
            <Tabs defaultValue="month1">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="day1">1 Day</TabsTrigger>
                <TabsTrigger value="week1">1 Week</TabsTrigger>
                <TabsTrigger value="month1">1 Month</TabsTrigger>
                <TabsTrigger value="year1">1 Year</TabsTrigger>
              </TabsList>

              {(['day1', 'week1', 'month1', 'year1'] as const).map(period => (
                <TabsContent key={period} value={period} className="mt-3">
                  <div className="space-y-1.5">
                    {sortedBySector(period).map(s => (
                      <div key={s.sector} className="flex items-center gap-2">
                        <div className="w-44 flex items-center gap-2">
                          {s.performance[period] >= 0
                            ? <TrendingUp className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                            : <TrendingDown className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                          <span className="text-xs font-medium text-foreground truncate">{s.sector}</span>
                          <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">{s.symbol}</Badge>
                        </div>
                        <div className="flex-1">
                          <PerformanceBar value={s.performance[period]} label="" />
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            {/* Stock Recommendations */}
            {recommendations.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <h3 className="text-base font-semibold text-foreground">
                      Undervalued Core Stocks — Buy Opportunities
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5">
                    {([
                      { value: 'both', label: 'All' },
                      { value: 'month1', label: '1M < 0%' },
                      { value: 'year1', label: '1Y < 0%' },
                    ] as const).map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setUnderperformFilter(opt.value)}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          underperformFilter === opt.value
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setSortByGap(v => !v)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors border ${
                      sortByGap
                        ? 'bg-primary text-primary-foreground shadow-sm border-primary'
                        : 'text-muted-foreground hover:text-foreground border-border'
                    }`}
                  >
                    Sort: Largest Gap
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Core stocks from sectors with negative 1-month or 1-year performance, trading below their 52-week highs.
                  Sorted by largest gap from ATH — potential mean reversion candidates.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredRecommendations.length === 0 ? (
                    <p className="text-sm text-muted-foreground col-span-2 text-center py-4">
                      No sectors match the selected filter.
                    </p>
                  ) : filteredRecommendations.map(rec => (
                    <Card key={rec.sector} className="border-red-500/20 bg-red-500/[0.02]">
                      <CardHeader className="pb-2 pt-3 px-4">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <TrendingDown className="h-4 w-4 text-red-500" />
                          {rec.sector}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-4 pb-3">
                        {rec.stocks.map(stock => (
                          <ATHGapBar key={stock.ticker} stock={stock} onSelect={setSelectedTicker} />
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {fetchedAt && (
              <p className="text-[10px] text-muted-foreground text-right flex items-center justify-end gap-2">
                {savedFromDb && (
                  <span className="inline-flex items-center gap-1 text-primary">
                    <Database className="h-3 w-3" /> Loaded from saved data
                  </span>
                )}
                Data fetched: {new Date(fetchedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>

    <StockTechnicalChart
      ticker={selectedTicker || ''}
      open={!!selectedTicker}
      onOpenChange={(open) => { if (!open) setSelectedTicker(null); }}
    />
    </>
  );
}
